import { ComponentTypesV2, SellerIntegrationType } from 'opensolar-common'
import { styled } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { FC, useMemo } from 'react'
import { FeaturedProducts } from './components'
import Blogs from './components/Blogs'
import Chat from './components/Chat'
import Cover from './components/Cover'
import Description from './components/Description'
import ExhibitResources from './components/ExhibitResources'
import Highlights from './components/Highlights'
import Navbar from './components/Navbar'
import Socials from './components/Socials'
import Statistics from './components/Statistics'
import Videos from './components/Videos'
import { BlogConfig, ContentBlock, ExpoLoggerType, StandData } from './expoStand'
import { useLoadFeaturedProducts } from './useLoadFeaturedProducts'
import { getFile, getHighlightBackgroundUrl, parseNavLinks, sanitizeHtml } from './utils'

interface PropTypes extends StandData {
  item: ContentBlock
  enabledContents: ContentBlock[]
  clickLogger?: ExpoLoggerType
  enabledSellerIntegrations?: SellerIntegrationType[]
}

const PaddedSection = styled('div', { name: 'PaddedSection' })({
  padding: 30,
  paddingBottom: 0,
})

const CustomContentWrapper = styled('div', { name: 'CustomContentWrapper' })({
  img: {
    width: '100% !important', //added the !important to override inline style from the html data content
    height: 'auto',
    display: 'block',
    objectFit: 'contain',
  },
})

const nonPaddedSections = ['navbar', 'cover', 'chat']
const dataNotRequired = ['resources', 'cover', 'navbar']
const nonPremiumComponents: string[] = ['our_story', 'navbar', 'featured_products']

const ComponentBlock: FC<PropTypes> = ({
  item,
  org,
  expoStand,
  enabledContents,
  clickLogger,
  enabledSellerIntegrations,
}) => {
  const translate = useTranslate()
  const assets = useMemo(() => expoStand?.contents?.assets, [expoStand?.contents])
  const partnerData = useMemo(
    () => ({
      partnerType: expoStand?.brand_content?.partner_type,
      manufacturers: expoStand?.brand_content?.manufacturers ?? [],
      hardwareSuppliers: expoStand?.brand_content?.hardware_suppliers ?? [],
      countriesInclude: expoStand?.countries_include ?? '',
    }),
    [
      expoStand?.brand_content?.partner_type,
      expoStand?.brand_content?.manufacturers,
      expoStand?.brand_content?.hardware_suppliers,
      expoStand?.countries_include,
    ]
  )
  const isPremium = org?.premium_expo

  const companyLogoUrl = useMemo(() => getFile(expoStand?.brand_content?.logo_contents) || org?.logo_public_url, [
    expoStand?.brand_content?.logo_contents,
    org?.logo_public_url,
  ])

  // Supports backward compatibility from `"component_type": <string>` to `"component_type": <string[]>`
  const componentType = useMemo(() => {
    if (item?.contentType !== 'featured_products') return []
    const featuredProductsData = item.data
    if (!featuredProductsData?.component_type) return []
    const types = Array.isArray(featuredProductsData.component_type)
      ? featuredProductsData.component_type
      : [featuredProductsData.component_type]
    return types as ComponentTypesV2[]
  }, [item])

  const featuredProductData =
    useLoadFeaturedProducts({
      partnerType: partnerData.partnerType,
      manufacturers: partnerData.manufacturers,
      hardwareSuppliers: partnerData.hardwareSuppliers,
      countriesInclude: partnerData.countriesInclude,
      componentType,
    }) ?? []

  const filteredEnabledContents = useMemo(() => {
    return enabledContents.filter(
      (content) => !(content.contentType === 'featured_products' && !featuredProductData.length)
    )
  }, [enabledContents, featuredProductData.length])

  const componentDisplay = useMemo(() => {
    if (
      !item ||
      !item.contentType ||
      !item.enabled ||
      (!isPremium && !nonPremiumComponents.includes(item.contentType)) ||
      (dataNotRequired.includes(item.contentType) ? false : !item.data)
    )
      return null
    switch (item.contentType) {
      case 'navbar': {
        const data = item?.data
        const navLinks = parseNavLinks(filteredEnabledContents) || []
        const secondaryCtaLabel = data?.secondaryCta?.label
        const secondaryCtaLink = data?.secondaryCta?.link
        return (
          <Navbar
            breadcrumbs={[
              { label: 'Expo', href: '/expo' },
              { label: expoStand?.brand_content?.name || org?.name || translate('Partner') },
            ]}
            cta={
              data?.cta?.label && data?.cta?.link
                ? {
                    label: data.cta.label,
                    link: data.cta.link,
                    newTab: data.cta.newTab,
                    onClick: clickLogger
                      ? () =>
                          clickLogger({
                            description: 'Navbar CTA clicked',
                            label: data.cta?.label,
                            link: data.cta?.link,
                          })
                      : undefined,
                  }
                : undefined
            }
            secondaryCta={
              secondaryCtaLabel && secondaryCtaLink
                ? {
                    label: secondaryCtaLabel,
                    link: secondaryCtaLink,
                    newTab: data?.secondaryCta?.newTab,
                    onClick: clickLogger
                      ? () =>
                          clickLogger({
                            description: 'Navbar secondary CTA clicked',
                            label: secondaryCtaLabel,
                            link: secondaryCtaLink,
                          })
                      : undefined,
                  }
                : undefined
            }
            links={navLinks}
          />
        )
      }
      case 'cover': {
        const data = item?.data
        const taglineData = { text: data?.tagline, whiteText: data?.whiteText }
        return (
          <Cover tagline={taglineData} logoUrl={companyLogoUrl} backgroundUrl={getFile(assets?.cover_background)} />
        )
      }
      case 'our_story': {
        const data = item?.data
        return (
          <Description
            body={data?.body || ''}
            tags={expoStand?.tags || []}
            videoUrl={org?.premium_expo ? data?.video : undefined}
            media={companyLogoUrl}
            clickLogger={
              clickLogger ? (url) => clickLogger({ description: 'Our Story Video Clicked', videoUrl: url }) : undefined
            }
          />
        )
      }
      case 'highlights': {
        return (
          <Highlights
            brandLogoUrl={companyLogoUrl}
            assets={assets}
            highlights={
              item?.data?.map((x) => {
                return {
                  ...x,
                  backgroundUrl: getHighlightBackgroundUrl(assets?.highlights, x.id),
                  cta: {
                    ...x.cta,
                    file: getFile(assets?.highlights?.[x.id]?.ctaFile),
                    onClick: clickLogger
                      ? () =>
                          clickLogger({
                            description: 'Highlight CTA clicked',
                            highlight: x.title,
                            ctaLabel: x.cta.label,
                          })
                      : undefined,
                  },
                }
              }) || []
            }
          />
        )
      }
      case 'stats': {
        return (
          <Statistics
            statistics={item?.data || []}
            partnerName={expoStand?.brand_content?.name || org?.name || 'Partner'}
          />
        )
      }
      case 'socials': {
        return (
          <Socials
            widgetLink={item?.data?.widgetLink}
            onClick={
              clickLogger
                ? () =>
                    clickLogger({
                      description: 'Socials widget clicked',
                    })
                : undefined
            }
          />
        )
      }
      case 'videos': {
        return (
          <Videos
            videos={item?.data || []}
            onVideoClick={
              clickLogger
                ? (url) =>
                    clickLogger({
                      description: 'Video card clicked',
                      videoUrl: url,
                    })
                : undefined
            }
          />
        )
      }
      case 'blogs': {
        return (
          <Blogs
            blogs={item?.data as BlogConfig[]}
            onBlogClick={
              clickLogger
                ? (url) =>
                    clickLogger({
                      description: 'Blog card clicked',
                      blogUrl: url,
                    })
                : undefined
            }
          />
        )
      }
      case 'resources': {
        return (
          <ExhibitResources
            standId={expoStand?.id}
            onExpand={
              clickLogger
                ? () =>
                    clickLogger({
                      description: 'Resources modal opened',
                    })
                : undefined
            }
            onResourceClick={
              clickLogger
                ? (url) =>
                    clickLogger({
                      description: 'Resource card clicked',
                      resourceUrl: url,
                    })
                : undefined
            }
          />
        )
      }
      case 'featured_products':
        if (!featuredProductData.length) return null
        return (
          <FeaturedProducts
            brandName={expoStand.brand_content.name}
            products={featuredProductData}
            standCountries={partnerData.countriesInclude || ''}
            enabledSellerIntegrations={enabledSellerIntegrations}
            onViewAllClick={
              clickLogger
                ? () =>
                    clickLogger({
                      description: 'View All Products clicked',
                    })
                : undefined
            }
            onProductClick={
              clickLogger
                ? (code) =>
                    clickLogger({
                      description: 'Product card clicked',
                      productCode: code,
                    })
                : undefined
            }
          />
        )
      case 'custom_content_1':
      case 'custom_content_2':
      case 'custom_content_3': {
        return <CustomContentWrapper dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.data?.content || '') }} />
      }
      case 'chat': {
        return <Chat apiKey={item?.data?.apiKey || ''} />
      }
      default:
        return null
    }
  }, [item, isPremium, featuredProductData])

  if (!componentDisplay) return null
  if (nonPaddedSections.includes(item.contentType)) return componentDisplay
  return <PaddedSection>{componentDisplay}</PaddedSection>
}
export default ComponentBlock
